import { useCallback, useMemo } from 'react';
import { useSpacetimeDB, useTable } from 'spacetimedb/react';
import { tables as minetwinTables, type DbConnection as MinetwinConn } from '@sdk/minetwin';
import type { NotificationItem, ToastKind } from '@/data/types';
import { toMillis } from '@/lib/toMillis';

/**
 * Durable notifications from the console module's `my_notifications` view —
 * the backend half of the topbar bell (the session half is retained toasts in
 * useToasts). Emitted server-side at three choke points: orientation assigned
 * (→ assignee), orientation completed (→ trainee congrats + project's
 * admins), first sign-in (→ welcome). Rows survive reloads and arrive even
 * if they were emitted while this person was offline.
 *
 * Read state lives server-side (`read_at`): opening the bell calls
 * `mark_my_notifications_read`, "Clear all" calls `dismiss_my_notifications`.
 * Both are fire-and-forget housekeeping — failures are console noise, never
 * toasts (a toast about the notification feed would itself land in the feed).
 */

/** Server `kind` → toast palette. Keep in sync with
 *  modules/minetwin/src/reducers/notifications.rs KIND_* constants. */
const KIND_MAP: Record<string, ToastKind> = {
  assignment: 'info',
  completion: 'good',
  welcome: 'good',
};

export function useDurableNotifications(): {
  items: NotificationItem[];
  unreadCount: number;
  markAllRead: () => void;
  clearAll: () => void;
} {
  const [rows] = useTable('minetwin', minetwinTables.my_notifications);
  const minetwinState = useSpacetimeDB('minetwin');

  const items = useMemo<NotificationItem[]>(
    () =>
      rows.map((r) => ({
        // Prefixed so ids can never collide with the session feed's random ids.
        id: `stdb-${r.id}`,
        title: r.title,
        sub: r.body || undefined,
        kind: KIND_MAP[r.kind] ?? 'info',
        ts: toMillis(r.createdAt),
        read: r.readAt != null,
        // Server-assigned auto_inc — the numeric tie-break for rows emitted
        // in the same transaction (same ts).
        seq: Number(r.id),
      })),
    [rows],
  );

  const unreadCount = useMemo(() => items.filter((n) => !n.read).length, [items]);

  const run = useCallback(
    (label: string, fn: (conn: MinetwinConn) => unknown) => {
      const conn = minetwinState.getConnection() as MinetwinConn | null;
      if (!conn) return; // offline — server state catches up next session
      try {
        // Reducer calls return promises: a rejection (SenderError, deploy
        // race "no such reducer", "No user row for caller") escapes a sync
        // try/catch and would surface as an unhandled-rejection toast —
        // exactly what this hook promises never to do. Consume it.
        Promise.resolve(fn(conn)).catch((err) =>
          console.warn(`[notifications] ${label} failed`, err),
        );
      } catch (err) {
        console.warn(`[notifications] ${label} failed`, err);
      }
    },
    [minetwinState],
  );

  const markAllRead = useCallback(() => {
    // No unread guard: the server treats it as a cheap no-op, and guarding
    // here would skip the backlog when the bell opens before the
    // subscription has delivered rows.
    run('mark read', (conn) => conn.reducers.markMyNotificationsRead({}));
  }, [run]);

  const clearAll = useCallback(() => {
    run('clear', (conn) => conn.reducers.dismissMyNotifications({}));
  }, [run]);

  return { items, unreadCount, markAllRead, clearAll };
}
